import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";

import { configureBifrost, getBifrost } from "@cars24/omniauth/bifrost";

import { isTokenExpired } from "../Shared/Utils/jwtUtils";

// Storage keys
const STORAGE_KEYS = {
  ACCESS_TOKEN: "accessToken",
  REFRESH_TOKEN: "refreshToken",
  SESSION_ID: "sessionId",
  USER_PROFILE: "userProfile",
  USER_ROLES: "userRoles",
  DEVICE_ID: "deviceId"
};

// Get or create a unique device ID for Bifrost
const getOrCreateDeviceId = () => {
  let deviceId = localStorage.getItem(STORAGE_KEYS.DEVICE_ID);

  if (!deviceId) {
    deviceId = `device-${crypto.randomUUID()}`;
    localStorage.setItem(STORAGE_KEYS.DEVICE_ID, deviceId);
  }

  return deviceId;
};

// Auth configuration from environment variables
const getClientId = () => process.env.REACT_APP_AUTH_CLIENT_ID;
const getAuthApiUrl = () => process.env.REACT_APP_AUTH_SERVICE_URL;
const getUserServiceUrl = () => process.env.REACT_APP_USER_SERVICE_URL;

// Create the context
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize Bifrost instance (lazy initialization)
  const [bifrost] = useState(() => {
    try {
      return getBifrost();
    } catch {
      return configureBifrost({
        authApiBaseUrl: getAuthApiUrl(),
        deviceId: getOrCreateDeviceId(),
        clientId: getClientId(),
        redirectUri: window.location.origin
      });
    }
  });

  // Store tokens in localStorage
  const storeTokens = useCallback((accessToken, refreshToken, sessionId) => {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
    localStorage.setItem(STORAGE_KEYS.SESSION_ID, sessionId);
  }, []);

  // Get stored tokens from localStorage
  const getStoredTokens = useCallback(
    () => ({
      accessToken: localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN),
      refreshToken: localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN),
      sessionId: localStorage.getItem(STORAGE_KEYS.SESSION_ID)
    }),
    []
  );

  // Clear all auth data from localStorage
  const clearAuthData = useCallback(() => {
    Object.values(STORAGE_KEYS).forEach((key) => {
      localStorage.removeItem(key);
    });
    // Also clear legacy keys
    localStorage.removeItem("ums-profile");
  }, []);

  // Fetch user profile and roles from user service
  const fetchUserProfile = useCallback(async (accessToken) => {
    try {
      const response = await fetch(`${getUserServiceUrl()}/api/v1/user`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json"
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch user profile: ${response.status}`);
      }

      const data = await response.json();

      if (data?.data) {
        const profile = {
          name: `${data.data.first_name || ""} ${data.data.last_name || ""}`.trim(),
          email: data.data.email,
          firstName: data.data.first_name,
          lastName: data.data.last_name,
          userId: data.user_id,
          userType: data.data.user_type,
          status: data.data.status,
          countryCode: data.data.country_code
        };
        const userRoles = data.data.roles || [];

        setUser(profile);
        setRoles(userRoles);
        localStorage.setItem(STORAGE_KEYS.USER_PROFILE, JSON.stringify(profile));
        localStorage.setItem(STORAGE_KEYS.USER_ROLES, JSON.stringify(userRoles));
      }
    } catch (error) {
      console.error("Failed to fetch user profile:", error);
    }
  }, []);

  // Login function - stores tokens and sets authenticated state
  const login = useCallback(
    async (accessToken, refreshToken, sessionId) => {
      storeTokens(accessToken, refreshToken, sessionId);
      setToken(accessToken);
      setIsAuthenticated(true);
      await fetchUserProfile(accessToken);
    },
    [storeTokens, fetchUserProfile]
  );

  // Logout function - calls Bifrost logout and clears all data
  const logout = useCallback(async () => {
    const { accessToken, sessionId } = getStoredTokens();

    if (sessionId && accessToken) {
      try {
        await bifrost.panelLogout({ sessionId, accessToken });
      } catch (error) {
        console.error("Logout API failed:", error);
      }
    }

    // Clear all stored data
    clearAuthData();
    localStorage.clear();
    sessionStorage.clear();

    // Reset state
    setToken(null);
    setIsAuthenticated(false);
    setUser(null);
    setRoles([]);

    // Redirect to login
    window.location.href = "/";
  }, [bifrost, getStoredTokens, clearAuthData]);

  // Get access token (for file uploads and other uses)
  const getAccessToken = useCallback(() => localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN), []);

  // Refresh token function
  const refreshToken = useCallback(async () => {
    const { refreshToken: storedRefreshToken, sessionId } = getStoredTokens();

    if (!storedRefreshToken || !sessionId) {
      return null;
    }

    try {
      const response = await bifrost.refreshToken({
        refreshToken: storedRefreshToken,
        sessionId
      });

      if (response?.data?.access_token) {
        storeTokens(response.data.access_token, response.data.refresh_token, response.data.session_id || sessionId);
        setToken(response.data.access_token);
        return response.data.access_token;
      }

      return null;
    } catch (error) {
      console.error("Token refresh failed:", error);
      return null;
    }
  }, [bifrost, getStoredTokens, storeTokens]);

  // Restore session on app load
  useEffect(() => {
    const restoreSession = async () => {
      setIsLoading(true);
      const { accessToken } = getStoredTokens();

      if (accessToken) {
        // Check if token is expired before restoring session
        if (isTokenExpired(accessToken)) {
          console.info("Token expired on app load, clearing session");
          clearAuthData();
          setIsLoading(false);
          return;
        }

        setToken(accessToken);
        setIsAuthenticated(true);

        // Restore user profile from localStorage if available
        const storedProfile = localStorage.getItem(STORAGE_KEYS.USER_PROFILE);
        if (storedProfile) {
          try {
            setUser(JSON.parse(storedProfile));
          } catch {
            // Invalid JSON, ignore
          }
        }

        // Restore user roles from localStorage if available
        const storedRoles = localStorage.getItem(STORAGE_KEYS.USER_ROLES);
        if (storedRoles) {
          try {
            setRoles(JSON.parse(storedRoles));
          } catch {
            // Invalid JSON, ignore
          }
        }
      }

      setIsLoading(false);
    };

    restoreSession();
  }, [getStoredTokens, clearAuthData]);

  // Helper function to check if user has a specific role
  const hasRole = useCallback((role) => roles.includes(role), [roles]);

  // Helper function to check if user has any of the specified roles
  const hasAnyRole = useCallback((roleList) => roleList.some((role) => roles.includes(role)), [roles]);

  const value = useMemo(
    () => ({
      // State
      isAuthenticated,
      isLoading,
      token,
      user,
      roles,

      // Bifrost instance (for login page)
      bifrost,

      // Functions
      login,
      logout,
      getAccessToken,
      refreshToken,
      getStoredTokens,
      hasRole,
      hasAnyRole
    }),
    [
      isAuthenticated,
      isLoading,
      token,
      user,
      roles,
      bifrost,
      login,
      logout,
      getAccessToken,
      refreshToken,
      getStoredTokens,
      hasRole,
      hasAnyRole
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Custom hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export default AuthContext;
